import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Phone, Check, X, Hourglass, Wallet, Copy } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  pending_availability: { label: 'Awaiting vendor response', icon: Hourglass, bg: 'bg-amber-50 border-amber-300', color: 'text-amber-800' },
  available:            { label: 'Confirmed available',      icon: Check,      bg: 'bg-green-50 border-green-300', color: 'text-green-700' },
  unavailable:          { label: 'Vendor unavailable',       icon: X,          bg: 'bg-red-50 border-red-300', color: 'text-red-700' },
};

/**
 * Customer-side inquiry tracker — one card per vendor-lane booking. Surfaces
 * each line's confirmation status, reveals the vendor phone once they mark
 * available, and shows a wallet-refund pill when they decline (the credit is
 * applied automatically — no further customer action required).
 */
const CustomerVendorInquiries = ({ booking, onChange }) => {
  const [inquiries, setInquiries] = useState([]);
  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/customer/vendor-bookings/${booking._id || booking.id}/inquiries`,
        { withCredentials: true });
      setInquiries(data?.inquiries || []);
    } catch { /* booking may not be dispatched yet */ }
  }, [booking._id, booking.id]);
  useEffect(() => { refresh(); }, [refresh]);

  if (!inquiries.length) return null;

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success(`Copied ${txt}`);
  };

  return (
    <Card className="border-[#E8E1D5] mb-4" data-testid={`vendor-inq-${booking._id || booking.id}`}>
      <CardHeader className="border-b border-[#E8E1D5] py-3">
        <CardTitle className="text-base font-heading">Vendor Confirmations</CardTitle>
        <CardDescription className="text-xs">
          {inquiries.filter(s => s.status === 'available').length} of {inquiries.length} confirmed available
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-3 space-y-2">
        {inquiries.map(s => {
          const meta = STATUS[s.status] || STATUS.pending_availability;
          const Icon = meta.icon;
          return (
            <div key={s.slot_id} className={`border-2 rounded p-3 ${meta.bg}`} data-testid={`vendor-slot-${s.slot_id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-sm">{s.name}</div>
                  <div className={`text-xs ${meta.color} flex items-center gap-1 mt-0.5 font-bold`}>
                    <Icon size={12} weight="bold" /> {meta.label}
                  </div>
                  {s.decline_reason && (
                    <div className="text-[11px] text-text-secondary mt-1 italic">"{s.decline_reason}"</div>
                  )}
                </div>
                <div className="text-right flex-shrink-0">
                  {s.advance_paid > 0 && s.status !== 'unavailable' && (
                    <div className="text-[10px] text-text-secondary">advance ₹{s.advance_paid}</div>
                  )}
                  {s.refunded_amount > 0 && (
                    <div className="text-[11px] text-green-700 font-bold flex items-center gap-1 mt-1">
                      <Wallet size={12} /> ₹{s.refunded_amount} → wallet
                    </div>
                  )}
                </div>
              </div>
              {s.status === 'available' && s.vendor_phone && (
                <button onClick={() => copy(s.vendor_phone)}
                  className="mt-2 w-full flex items-center justify-between gap-2 p-2 bg-white border-2 border-green-400 rounded hover:bg-green-50 transition-colors"
                  data-testid={`reveal-phone-${s.slot_id}`}>
                  <span className="flex items-center gap-2">
                    <Phone size={14} className="text-green-700" />
                    <span className="text-sm font-mono font-bold">{s.vendor_phone}</span>
                  </span>
                  <Copy size={12} className="text-text-secondary" />
                </button>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default CustomerVendorInquiries;
